import { type ReactNode, createContext, useCallback, useEffect, useMemo, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";

import { type PubsubVirtualClassStateMessageFromClientType } from "../shared/types/av-state";
import { MonitoringEventTypeType } from "../shared/types/monitoring";
import { WebsocketStatusType } from "../shared/types/pubsub";
import { setupSocketListeners } from "../utils/pubsub";

import { PingComponent } from "./PingComponent";

import { t } from "i18n/i18n";
import { useAppDispatch, useAppSelector } from "store/index";
import { clearPubsubAPICache } from "store/pubsub-slice";
import { selectCurrentSectionId, selectCurrentUserId, selectUserJWT } from "store/selectors";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";
import { logInfoWithDebugFlag } from "utils/logger";
import { useViewLevel } from "utils/urls";

// The minimum delay should be longer than the longest time we expect congested
// traffic to delay a message. Otherwise, the connection will try to reconnect
// in a loop.
const MIN_RECONNECTION_DELAY_MS = 3000;
// The maximum number of times we will try to reconnect before giving up
const MAX_RETRY_TIMES = 100;

// eslint-disable-next-line react-refresh/only-export-components
export const latestSocketConnection: { socket: ReconnectingWebSocket | null } = {
  socket: null,
};

// TODO: This should be unified with ConnectionStatusIndicator states
// eslint-disable-next-line react-refresh/only-export-components
export const PUBSUB_CONNECTION_STATUS_MESSAGE: Record<WebsocketStatusType, string> = {
  connecting: t("websocket.connecting"),
  connected: t("websocket.connected"),
  disconnected: t("websocket.disconnected"),
  closed: t("websocket.closed"),
  stopped: t("websocket.stopped"),
  401: t("websocket.401"),
};

export interface PubsubWebsocketContextType {
  pubsubStatus: WebsocketStatusType;
  pubsubStatusReason: string;
  pubsubSectionId: string;
  sendPubsubMessage: (data: PubsubVirtualClassStateMessageFromClientType) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const PubsubWebsocketContext = createContext<PubsubWebsocketContextType>({
  pubsubStatus: "connecting",
  pubsubStatusReason: "",
  pubsubSectionId: "",
  sendPubsubMessage: () => {},
});

/**
 * PubsubWebsocketProvider manages the websocket connection for non-ShareDB messages:
 * - Model updates from Django
 * - Auth updates
 * - Attendance updates
 * - Virtual class state
 *
 * This is separate from WebsocketProvider (ShareDB) to prevent pubsub traffic
 * from blocking or delaying latency-sensitive OT operations.
 */
export const PubsubWebsocketProvider = ({ children }: { children: ReactNode }) => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const jwt = useAppSelector(selectUserJWT);
  const userId = useAppSelector(selectCurrentUserId);
  const isHomeView = useViewLevel().home;

  const [websocketErrorCode, setWebsocketErrorCode] = useState<"401" | null>(null);
  const [pubsubStatus, setPubsubStatus] = useState<WebsocketStatusType>("connecting");
  const [pubsubStatusReason, setPubsubStatusReason] = useState<string>("");
  const [pubsubSectionId, setPubsubSectionId] = useState<string>("");
  const [webSocket, setWebSocket] = useState<ReconnectingWebSocket | null>(null);
  const dispatch = useAppDispatch();

  const createMonitoringEvent = useCreateMonitoringEvent();

  const sendPubsubMessage = useCallback(
    (message: PubsubVirtualClassStateMessageFromClientType) => {
      if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
        logInfoWithDebugFlag(
          "debug_virtual_class",
          "Pubsub websocket is not open, cannot send message",
          message,
        );
        return;
      }

      logInfoWithDebugFlag("debug_virtual_class", "Sending message via pubsub websocket", message);
      webSocket.send(JSON.stringify(message));
    },
    [webSocket],
  );

  // Compute status from error code or connection state
  const computedStatus: WebsocketStatusType = websocketErrorCode || pubsubStatus;

  const contextValue = useMemo(
    () => ({
      pubsubStatus: computedStatus,
      pubsubStatusReason,
      pubsubSectionId,
      sendPubsubMessage,
    }),
    [computedStatus, pubsubStatusReason, sendPubsubMessage, pubsubSectionId],
  );

  useEffect(() => {
    if (!jwt || !userId) return;
    if (!sectionId && !isHomeView) return;

    // Clear the latest socket state immediately when starting a new connection
    // This ensures old connection handlers see the state is cleared even if they fire
    // synchronously during cleanup
    latestSocketConnection.socket = null;

    if (webSocket) {
      logInfoWithDebugFlag("debug_pubsub", "Closing old pubsub socket before creating a new one", {
        sectionId,
      });

      // We don't guarantee objects outside of our current websocket course scope to be updated via pubsub, so caches
      // might get stale. We clear them on scope change to prevent that.
      // TODO: We might want to make this more granular, e.g. keep org-scoped data in the cache.
      clearPubsubAPICache();

      // Close the old socket to prevent the socket from reconnecting.
      // This handles the case where a socket was reconnected after cleanup already ran but where the socket is
      // orphaned.
      webSocket.close();
    }

    const socketUrl = `${window._env_.PUBSUB_WEBSOCKET_URL}?jwt=${jwt}&section_id=${sectionId}`;
    const socket = new ReconnectingWebSocket(`${socketUrl}`, [], {
      maxRetries: MAX_RETRY_TIMES,
      maxEnqueuedMessages: 0,
      minReconnectionDelay: MIN_RECONNECTION_DELAY_MS,
    });

    setWebSocket(socket);

    // Setup socket event listeners for pubsub messages
    setupSocketListeners(socket, {
      sectionId,
      userId,
      dispatch,
      onError: setWebsocketErrorCode,
    });

    // Store the active socket in the global state so we can track it
    latestSocketConnection.socket = socket;

    // Track connection status
    const handleOpen = () => {
      // Only update status if this is still the active connection
      if (latestSocketConnection.socket === socket) {
        logInfoWithDebugFlag("debug_pubsub", "Pubsub websocket connected", { sectionId, userId });
        setPubsubStatus("connected");
        setPubsubStatusReason("");
        setPubsubSectionId(sectionId || "");
      } else {
        logInfoWithDebugFlag("debug_pubsub", "Ignoring pubsub websocket open for old socket", {
          sectionId,
          userId,
        });
      }
    };

    const handleClose = (event: CloseEvent) => {
      // Only update status if this is still the active connection
      if (latestSocketConnection.socket === socket) {
        logInfoWithDebugFlag("debug_pubsub", "Pubsub websocket closed", {
          sectionId,
          userId,
          code: event.code,
          reason: event.reason,
        });
        setPubsubSectionId(sectionId || "");
        setPubsubStatus("disconnected");
        setPubsubStatusReason(event.reason || "Connection closed");
      } else {
        logInfoWithDebugFlag("debug_pubsub", "Old pubsub websocket closed", { sectionId, userId });
      }
    };

    const handleError = () => {
      // Only update status if this is still the active connection
      if (latestSocketConnection.socket === socket) {
        logInfoWithDebugFlag("debug_pubsub", "Pubsub websocket error", { sectionId, userId });
        setPubsubStatus("disconnected");
        setPubsubStatusReason("Connection error");
      } else {
        logInfoWithDebugFlag("debug_pubsub", "Old pubsub websocket error", { sectionId, userId });
      }
    };

    socket.addEventListener("open", handleOpen);
    socket.addEventListener("close", handleClose);
    socket.addEventListener("error", handleError);

    // Initialize status
    setPubsubStatus("connecting");
    setPubsubSectionId(sectionId || "");

    return () => {
      socket.removeEventListener("open", handleOpen);
      socket.removeEventListener("close", handleClose);
      socket.removeEventListener("error", handleError);

      // Clear global state before closing so old handlers don't see themselves as active
      // even if they fire synchronously during close()
      if (latestSocketConnection.socket === socket) {
        latestSocketConnection.socket = null;
      }

      logInfoWithDebugFlag("debug_pubsub", "Closing pubsub websocket due to unmount", {
        sectionId,
      });
      socket.close();
    };

    // This component only uses webSocket to close it, and also sets it,
    // so we don't want to cause a loop by re-rendering when it changes.
    // eslint-disable-next-line no-object-deps/no-object-deps, react-hooks/exhaustive-deps
  }, [jwt, userId, sectionId, isHomeView, dispatch]);

  // Track pubsub connection status changes
  useEffect(() => {
    // TODO: if we support creating monitoring events from the home page, we can remove this check.
    if (!pubsubSectionId) return;

    if (pubsubStatus === "connected") {
      createMonitoringEvent(MonitoringEventTypeType.WEBSOCKET_CONNECTED, {
        connection: "pubsub",
      });
    } else if (pubsubStatus === "disconnected") {
      // Active connection disconnected - this is unexpected
      if (document.hasFocus()) {
        createMonitoringEvent(MonitoringEventTypeType.WEBSOCKET_DISCONNECTED, {
          reason: pubsubStatusReason,
          connection: "pubsub",
        });
      }
    } else if (pubsubStatus !== "connecting") {
      createMonitoringEvent(MonitoringEventTypeType.WEBSOCKET_ERROR, {
        websocketStatus: pubsubStatus,
        connection: "pubsub",
      });
    }

    // We only want to create these events when the websocket status changes and not
    // when hasFocus or pubsubStatusReason changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubsubStatus, createMonitoringEvent]);

  return (
    <>
      {webSocket && <PingComponent connectionType="pubsub" socket={webSocket} />}
      <PubsubWebsocketContext.Provider value={contextValue}>
        {children}
      </PubsubWebsocketContext.Provider>
    </>
  );
};
